import { Percent } from "@mui/icons-material";
import {
  DialogActions,
  DialogContent,
  DialogProps,
  InputAdornment,
  Typography,
} from "@mui/material";
import api from "app/api";
import {
  Button,
  ButtonGroup,
  DialogTitle,
  globalMessageHandler,
  Spacer,
  TextField,
  Toolbar,
} from "common";
import Price from "common/components/Inputs/Price/Price";
import I18n from "i18n";
import { useState } from "react";

export interface RefundBookingListModalInnerProps extends DialogProps {
  bookingIds?: number[];
  refund?: number | null;
  afterRefund?: () => void;
}

function RefundBookingListModalInner({
  bookingIds,
  afterRefund,
  refund,
  ...props
}: RefundBookingListModalInnerProps) {
  const isGroup = bookingIds && bookingIds?.length > 1;

  const [refundBookingLoading, setRefundBookingLoading] = useState(false);

  function refundBookingsAsync() {
    globalMessageHandler.confirm(
      {
        title: `Refund módosítás megerősítése`,
        message: `Biztosan modosítod a refund értkét ${bookingIds?.length} foglalás esetén?`,
        severity: "info",
      },
      async () => {
        setRefundBookingLoading(true);
        const { success, data, error } = await api.post("bookings/refund", {
          booking_ids: bookingIds,
          amount: refundAmount,
          unit: refundUnit,
          reason: refundReason,
        });
        setRefundBookingLoading(false);
        if (success) {
          globalMessageHandler.snack({
            message: "Sikeres refund állítás.",
          });
          afterRefund && afterRefund();
        } else {
        }
      }
    );
  }

  const [refundUnit, setRefundUnit] = useState<"amount" | "percent">("percent");
  const [refundReason, setRefundReason] = useState<string | undefined>();
  const [refundAmount, setRefundAmount] = useState<number | null | undefined>(
    refund
  );

  return (
    <>
      <DialogTitle sx={{ position: "relative" }} onClose={props.onClose}>
        {isGroup ? "Csoportos refund módosítás" : "Refund módosítás"}
      </DialogTitle>
      <DialogContent>
        {isGroup && (
          <Typography>{`Refund csoportos módosítása ${bookingIds?.length} foglalás esetén.`}</Typography>
        )}
        <Spacer h={2} />
        <Toolbar sx={{ gap: 1 }}>
          <ButtonGroup
            sx={{
              flex: 0,
              minWidth: 220,
              maxWidth: 220,
              "& .MuiButton-root.MuiButtonGroup-grouped": {},
            }}
            size="small"
            value={refundUnit}
            options={[
              { value: "percent", label: "Százalék" },
              { value: "amount", label: "Összeg" },
            ]}
            onChange={(evt: any) => {
              setRefundUnit(evt.target.value);
              setRefundAmount(undefined);
            }}
          />
          <Spacer h={2} />
          {refundUnit === "amount" && (
            <Price
              label={I18n.t("Booking.attributes.refund_amount")}
              mode={"normal"}
              count={1}
              value={refundAmount}
              onChange={(evt: any) => setRefundAmount(evt.target.value)}
            />
          )}
          {refundUnit === "percent" && (
            <TextField
              label={I18n.t("Booking.attributes.refund_percent")}
              mask={Number}
              numberOptions={{
                scale: 2,
                min: 0,
                max: 100,
              }}
              unmask
              value={refundAmount}
              onChange={(evt: any) => setRefundAmount(evt.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Percent />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Toolbar>
        <Spacer h={2} />
        <TextField
          label={I18n.t("Booking.attributes.refund_reason")}
          value={refundReason}
          onChange={(evt: any) => setRefundReason(evt.target.value)}
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          title={I18n.t("App.cancel")}
          variant="outlined"
          onClick={() => {
            props.onClose && props.onClose({}, "backdropClick");
          }}
        />
        <Spacer right />
        <Button
          title="Mentés"
          onClick={() => refundBookingsAsync()}
          loading={refundBookingLoading}
          disabled={refundBookingLoading}
        />
      </DialogActions>
    </>
  );
}

export default RefundBookingListModalInner;
